import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../../../components/element/Loader";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../../services/StorageProvider";
import { ClearanceStatus, TRIP_PHASE } from "../../../../utils/constants";
import { getAirportData } from "../../../../utils/utils";
import "./EscortDetails.css";
import EscortArrivalList from "./views/EscortArrivalList";
import EscortArrivalOfficersList from "./views/EscortArrivalOfficersList";
import EscortDepartureActionButtons from "./views/EscortDepartureActionButtons";
import EscortDepartureOfficersList from "./views/EscortDepartureOfficersList";
import EscortDetailsHeader from "./views/EscortDetailsHeader";
import EscortPassengersList from "./views/EscortPassengerList";
import EscortVehicleList from "./views/EscortVehicleList";
import FlightNumber from "./views/FlightNumber";
import { useFormData } from "../../../../helpers/FormDataProvider";

const EscortDetails = () => {
  const { userData } = useFormData();
  const { getEscortDetails, updateEscortOfficers } = useAirlineAdminApi(
    userData.isAAUser
  );
  const { airportData } = useContext(StorageContext);

  const { flightNumber, flightId } = useParams();

  const { data: flightDetails, isLoading } = useQuery({
    queryKey: ["specialOnboarding", flightNumber],
    queryFn: () => getEscortDetails(flightId),
  });

  const [flightPlanDetail, setFlightPlanDetail] = useState(null);

  const [arrivalOfficers, setArrivalOfficers] = useState([]);
  const [departureOfficers, setDepartureOfficers] = useState([]);
  const storedData = localStorage.getItem("flightPlanData");
  useEffect(() => {
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      // Extract flight plan data from the nested structure
      const flightPlan = parsedData.cfp || parsedData;
      setFlightPlanDetail(flightPlan);
    }
  }, []);

  const { mutate: updateEscortOfficersMutate } = useMutation({
    mutationFn: ({ flightPlanId, officers, cfpPhase }) =>
      updateEscortOfficers(flightPlanId, officers, cfpPhase),
    onSuccess: (response) => {},
    onError: (error) => {
      console.error("Error adding officer", error);
    },
  });

  useEffect(() => {
    if (flightDetails?.data?.officers) {
      const filteredArrivalOfficers = flightDetails?.data?.officers?.filter(
        (officer) => officer.cfpPhase === TRIP_PHASE.ARRIVAL
      );

      const filteredDepartureOfficers = flightDetails?.data?.officers?.filter(
        (officer) => officer.cfpPhase === TRIP_PHASE.DEPARTURE
      );

      setArrivalOfficers(filteredArrivalOfficers);
      setDepartureOfficers(filteredDepartureOfficers);
    }
  }, [flightDetails?.data?.officers]);

  const handleAddArrivalOfficer = (officers) => {
    updateEscortOfficersMutate({
      flightPlanId: flightId,
      officers: officers,
      cfpPhase: TRIP_PHASE.ARRIVAL,
    });
    setArrivalOfficers((prevOfficers) => [...prevOfficers, ...officers]);
  };
  const handleEditArrivalOfficer = (index, updatedOfficer) => {
    const updatedOfficers = [...arrivalOfficers];
    updatedOfficers[index] = updatedOfficer;

    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      cfpPhase: TRIP_PHASE.ARRIVAL,
      hoursWorked: officer.hoursWorked,
    }));

    updateEscortOfficersMutate({
      flightPlanId: flightId,
      officers: officersData,
      cfpPhase: TRIP_PHASE.ARRIVAL,
    });
    setArrivalOfficers(updatedOfficers);
  };

  const handleAddDepartureOfficer = (officers) => {
    updateEscortOfficersMutate({
      flightPlanId: flightId,
      officers: officers,
      cfpPhase: TRIP_PHASE.DEPARTURE,
    });
    setDepartureOfficers((prevOfficers) => [...prevOfficers, ...officers]);
  };

  const handleEditDepartureOfficer = (index, updatedOfficer) => {
    const updatedOfficers = [...departureOfficers];
    updatedOfficers[index] = updatedOfficer;

    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      cfpPhase: TRIP_PHASE.DEPARTURE,
      hoursWorked: officer.hoursWorked,
    }));

    updateEscortOfficersMutate({
      flightPlanId: flightId,
      officers: officersData,
      cfpPhase: TRIP_PHASE.DEPARTURE,
    });
    setDepartureOfficers(updatedOfficers);
  };
  const shouldShowArrivalSection =
    (flightDetails?.data?.escortService?.requiresOnArrival &&
      flightDetails?.data?.escortService?.arrivalStatus ===
        ClearanceStatus.CLEARANCE_REQUESTED) ||
    flightDetails?.data?.escortService?.arrivalStatus ===
      ClearanceStatus.CLEARANCE_REJECTED ||
    flightDetails?.data?.escortService?.arrivalStatus ===
      ClearanceStatus.CLEARANCE_GRANTED;

  const shouldShowDepartureSection =
    (flightDetails?.data?.escortService?.requiresOnDeparture &&
      flightDetails?.data?.escortService?.departureStatus ===
        ClearanceStatus.CLEARANCE_REQUESTED) ||
    flightDetails?.data?.escortService?.departureStatus ===
      ClearanceStatus.CLEARANCE_REJECTED ||
    flightDetails?.data?.escortService?.departureStatus ===
      ClearanceStatus.CLEARANCE_GRANTED;

  const arrivalVehicles =
    flightDetails?.data?.vehicles.filter(
      (vehicle) => vehicle.tripPhase === TRIP_PHASE.ARRIVAL
    ) || [];
  const departureVehicles =
    flightDetails?.data?.vehicles.filter(
      (vehicle) => vehicle.tripPhase === TRIP_PHASE.DEPARTURE
    ) || [];

  const handleDeleteArrivalOfficer = (index) => {
    setArrivalOfficers((prevOfficers) =>
      prevOfficers.filter((_, i) => i !== index)
    );
  };
  const handleDeleteDepartureOfficer = (index) => {
    setDepartureOfficers((prevOfficers) =>
      prevOfficers.filter((_, i) => i !== index)
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="escort-details-container">
        <EscortDetailsHeader />

        <div className="escort-departure-airport-name">
          {
            getAirportData(airportData, flightPlanDetail?.departureAirportId)
              ?.name
          }
          (Departures)
        </div>

        <div className="escort-details-row">
          <div className="escort-details-left-column">
            <FlightNumber id={flightNumber} />
            <EscortPassengersList
              passengers={flightDetails?.data?.passengers}
            />
            {shouldShowDepartureSection && (
              <EscortDepartureOfficersList
                officers={departureOfficers}
                onAddOfficer={handleAddDepartureOfficer}
                onDeleteOfficer={handleDeleteDepartureOfficer}
                airportId={flightPlanDetail?.departureAirportId}
                onEditOfficer={handleEditDepartureOfficer}
                clearanceStatus={
                  flightDetails?.data?.escortService?.departureStatus
                }
              />
            )}
          </div>

          <div className="escort-details-left-column">
            <div className="escort-departure-vehicle-list">
              <EscortVehicleList vehicles={departureVehicles} />
            </div>
          </div>
        </div>

        {shouldShowDepartureSection && (
          <EscortDepartureActionButtons
            flightPlanId={flightId}
            clearanceStatus={
              flightDetails?.data?.escortService?.departureStatus
            }
            officers={departureOfficers}
          />
        )}

        <div className="escort-details-row">
          <div className="escort-details-left-column">
            <EscortArrivalList
              arrivalVehicles={arrivalVehicles}
              officers={arrivalOfficers}
              tripData={flightDetails?.data}
              airportName={
                getAirportData(airportData, flightPlanDetail?.arrivalAirportId)
                  ?.name
              }
            />
          </div>
          {shouldShowArrivalSection && (
            <div className="escort-details-left-column">
              <EscortArrivalOfficersList
                officers={arrivalOfficers}
                onAddOfficer={handleAddArrivalOfficer}
                onDeleteOfficer={handleDeleteArrivalOfficer}
                airportId={flightPlanDetail?.arrivalAirportId}
                onEditOfficer={handleEditArrivalOfficer}
                clearanceStatus={
                  flightDetails?.data?.escortService?.arrivalStatus
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscortDetails;
